import {Box, Stack, Typography, useTheme} from '@mui/material';
import React, {Fragment, MouseEvent, useState} from 'react';

import {BeneficiarySituationEnum} from 'domain/usecases/others/selection-flow/beneficiary-v3';
import {SelectionUserPlanEnum} from 'main/analytics/gaTagsEnum';
import {AlertActions, AlertModal} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import Icon from 'presentation/icons';
import {translations} from 'presentation/translations-new';
import {formatText, getInitials} from 'presentation/utils';
import {
	IconContainer,
	ProfileIcon,
	StyledButton,
	StyledCard,
	StyledIconButton,
	StyledTypography,
} from './styles';
import {BeneficiaryCardProps} from './types';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {FeaturesEnum, UserTypeEnum} from 'presentation/constants';
import {useMediaQueries} from 'presentation/pages/private';
import {AlertCancelled} from 'presentation/components/AlertCancelled';

const {cancelIn, cancelContract, responsibleWithoutBenefits, understood} =
	translations['pt-br'].commons;
const {
	selected: selectedLabel,
	select,
	blockedBeneficiary,
} = translations['pt-br'].others.selectionFlow;

export function BeneficiaryCard({
	selected,
	onSelect,
	beneficiary,
	allCancelled,
	blocked,
}: BeneficiaryCardProps) {
	const {isTablet} = useMediaQueries();

	const {palette, colors} = useTheme();

	const [showBlockedInfoModal, setShowBlockedInfoModal] = useState(false);

	const {isEnabled: isNewUIEntryFlowIsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_NEW_UI_ENTRY_FLOW,
	);

	const {
		nmSocialUsuarioC,
		nmUsuarioC,
		dtCancelamentoC,
		situacao,
		tipoUsuarioC,
		flTitularSemDireito,
	} = beneficiary;

	const isResponsibleWithoutBenefits =
		tipoUsuarioC === UserTypeEnum.HOLDER && flTitularSemDireito;
	const IS_CANCELLED = situacao === BeneficiarySituationEnum.CANCELLED;
	const shouldHasBorder = isNewUIEntryFlowIsEnabled && IS_CANCELLED && !selected;
	const shouldShowAlert = !isNewUIEntryFlowIsEnabled && IS_CANCELLED && !allCancelled;

	const handleSelect = (e: MouseEvent) => {
		e.stopPropagation();
		if (blocked) return;
		onSelect(beneficiary);
	};

	const handleShowBlockedInfo = (e: MouseEvent) => {
		e.stopPropagation();
		setShowBlockedInfoModal(true);
	};

	const cardIcon = blocked ? (
		<StyledIconButton onClick={handleShowBlockedInfo} ml="auto">
			<Icon
				aria-label={blockedBeneficiary.title}
				name="info-outlined"
				color={colors.gray['50Percent']}
				size={20}
			/>
		</StyledIconButton>
	) : (
		<Icon name="arrow-right-filled" color={palette.primary.main} size={20} />
	);

	return (
		<>
			<StyledCard
				data-tag-id={SelectionUserPlanEnum.SELECIONOU_TITULAR}
				elevation={selected ? 1 : 0}
				selected={selected}
				onClick={handleSelect}
				sx={{
					border: shouldHasBorder ? `1px solid ${palette.divider}` : undefined,
					flexDirection: 'column',
				}}>
				{shouldShowAlert ? (
					<Box sx={{width: '100%'}}>
						<AlertCancelled dtCancelamento={dtCancelamentoC} />
					</Box>
				) : null}
				<Stack
					flexDirection={'row'}
					justifyContent={'space-between'}
					alignItems={'center'}
					width={'100%'}>
					<Stack
						direction="row"
						alignItems="center"
						gap={1}
						overflow="hidden"
						width={'100%'}>
						{isNewUIEntryFlowIsEnabled ? (
							<Fragment>
								<ProfileIcon $blocked={blocked}>
									{getInitials(
										beneficiary?.nmSocialUsuarioC || beneficiary?.nmUsuarioC,
									)}
								</ProfileIcon>
								<Stack>
									<Typography variant="caption">
										{formatText(tipoUsuarioC)}
									</Typography>
									<StyledTypography variant="body2">
										{formatText(nmSocialUsuarioC || nmUsuarioC)}
									</StyledTypography>

									{isResponsibleWithoutBenefits ? (
										<Typography variant="caption">
											{responsibleWithoutBenefits.replace('.', '')}
										</Typography>
									) : null}

									{IS_CANCELLED ? (
										<Stack direction="row" alignItems="center" gap={0.5} mt={1}>
											<Icon
												name="warning-outlined"
												color={palette.warning.main}
												size={14}
											/>
											<Typography variant="caption">
												{dtCancelamentoC ? cancelIn : cancelContract}
												<strong>{dtCancelamentoC}</strong>
											</Typography>
										</Stack>
									) : null}
								</Stack>
							</Fragment>
						) : (
							<Fragment>
								<IconContainer>
									<Icon name="person-filled" size={20} />
								</IconContainer>
								<StyledTypography variant="body2">
									{formatText(nmSocialUsuarioC || nmUsuarioC)}
								</StyledTypography>
							</Fragment>
						)}

						{isNewUIEntryFlowIsEnabled ? null : (
							<Box sx={{ml: 'auto'}}>
								{isTablet && selected ? (
									<StyledIconButton
										color="success"
										onClick={handleSelect}
										ml="auto">
										<Icon name="success" size={24} />
									</StyledIconButton>
								) : (
									<StyledButton
										id="select-beneficiary"
										endIcon={
											selected ? <Icon name="success" size={20} /> : null
										}
										color={selected ? 'success' : 'primary'}
										variant="text"
										onClick={handleSelect}
										ml="auto"
										$selected={selected}>
										{selected ? selectedLabel : select}
									</StyledButton>
								)}
							</Box>
						)}
					</Stack>

					{isNewUIEntryFlowIsEnabled ? (
						<Box sx={{alignSelf: 'baseline'}}>
							{selected ? (
								<Loading size={20} color="primary" fullWidth={false} />
							) : (
								cardIcon
							)}
						</Box>
					) : null}
				</Stack>
			</StyledCard>

			<AlertModal
				open={showBlockedInfoModal}
				variant="info"
				onClose={() => setShowBlockedInfoModal(false)}
				headerTitle={blockedBeneficiary.headerTitle}
				title={blockedBeneficiary.title}
				description={
					<>
						{blockedBeneficiary.descriptionPart1}{' '}
						<strong>{blockedBeneficiary.descriptionPart2}</strong>
					</>
				}
				footerContent={
					<AlertActions
						primaryAction={() => setShowBlockedInfoModal(false)}
						primaryText={understood}
					/>
				}
				showIcon
				customIconName="info-filled"
			/>
		</>
	);
}
